import type { FilterRuleModel } from '@/shared/generated'
import type { MouseEvent } from 'react'

import { flatten, map, pipe, size, uniq, uniqBy } from 'lodash/fp'
import { PencilIcon, PlusIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Operators } from '@/shared/components/query-builder'
import { Button } from '@/shared/components/ui/button'
import {
	Item,
	ItemActions,
	ItemContent,
	ItemDescription,
	ItemTitle,
} from '@/shared/components/ui/item'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Spinner } from '@/shared/components/ui/spinner'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { useLocationQuery } from '@/shared/hooks/useLocationQuery'
import { ampli } from '@/shared/lib/ampli'
import { useModals } from '@/shared/lib/modal-manager'
import { getFilterValueByName } from '@/shared/tools/filtering'

import { useSchedulesQuery } from '../api/queries'

const groupsCount = pipe([map('optimization_groups'), flatten, uniq, size])

export function OptimizationScheduleWidget() {
	const { t } = useTranslation()

	const { showModal } = useModals()
	const [{ filters }, setQuery] = useLocationQuery()
	const isActive = Boolean(getFilterValueByName(filters, 'schedule.id'))
	const schedules = useSchedulesQuery()
	const handleCreateNewClick = (e: MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation()
		showModal('OPTIMIZATION_SCHEDULE_MODAL')
	}
	const handleShowListClick = (e: MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation()
		showModal('OPTIMIZATION_SCHEDULE_LIST_MODAL')
	}
	const handleFilterOgsClick = (e: MouseEvent<HTMLDivElement>) => {
		e.preventDefault()
		ampli.autorunFilterScheduledOGs()
		setQuery({
			filters: isActive
				? uniqBy(
						'name',
						filters.filter((f: FilterRuleModel) => f.name !== 'schedule.id')
					)
				: uniqBy('name', [
						{
							name: 'schedule.id',
							operation: Operators.IS_NOT_EMPTY,
							value: 1,
						},
						...filters,
					]),
		})
	}
	if (schedules.data?.length == 0) {
		if (schedules.isLoading) {
			return <Skeleton className='h-10 w-[120px]' />
		}

		return (
			<Button variant='dashed' onClick={handleCreateNewClick}>
				<PlusIcon />
				{t('schedule_repricing')}
			</Button>
		)
	} else {
		return (
			<Item
				size='sm'
				variant='muted'
				onClick={handleFilterOgsClick}
				data-active={isActive}
				className='cursor-pointer rounded-lg transition-colors duration-100 hover:bg-tertiary/50 data-[active=true]:bg-tertiary'
			>
				<ItemContent>
					<ItemTitle>{t('general_schedule')}</ItemTitle>
					<ItemDescription className='text-xs font-medium'>
						{schedules.isLoading ? <Spinner /> : groupsCount(schedules.data)}{' '}
						{t('scheduled_ogs')}
					</ItemDescription>
				</ItemContent>
				<ItemActions>
					<Tooltip>
						<TooltipTrigger asChild>
							<Button size='icon-sm' onClick={handleCreateNewClick}>
								<PlusIcon />
							</Button>
						</TooltipTrigger>
						<TooltipContent>{t('general_create')}</TooltipContent>
					</Tooltip>
					<Tooltip>
						<TooltipTrigger asChild>
							<Button size='icon-sm' onClick={handleShowListClick}>
								<PencilIcon />
							</Button>
						</TooltipTrigger>
						<TooltipContent>{t('general_edit')}</TooltipContent>
					</Tooltip>
				</ItemActions>
			</Item>
		)
	}
}
